import React, { memo, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { CloudOff } from '@material-ui/icons';
import { ChapterList, Message } from 'components';
import NotesHighlightsListItem from './NotesHighlightsListItem';
import {
  NetworkStatusValuesContext,
  UserContentValuesContext,
  UserSessionValuesContext,
} from 'contexts';

const useStyles = makeStyles((theme) => ({
  root: {
    fontFamily: theme.typography.fontFamily,
    width: '100%',
    maxWidth: 256,
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

const messageProps = {
  text: `You're currently offline. Links to content that have not been downloaded are unavailable. Once you're online, your notes and highlights will automatically sync.`,
  icon: <CloudOff />,
  shouldHaveAction: false,
  shouldHaveBorderRadius: false,
};

const createNotesAndHighlightsList = (isOffline, user, userContent) => {
  if (!userContent?.notesAndHighlightsByTopLevelMarker) {
    return null;
  }

  const notesAndHighlightsByTopLevelMarker = Array.from(
    userContent.notesAndHighlightsByTopLevelMarker,
  );

  if (isOffline) {
    // Collaboration content should not be included when offline
    return notesAndHighlightsByTopLevelMarker.map(([key, values]) => [
      key,
      values.filter((annotation) => user.created(annotation)),
    ]);
  }

  return notesAndHighlightsByTopLevelMarker;
};

export const NotesHighlightsMenu = ({ defaultExpanded = false, ...props }) => {
  const classes = useStyles();

  const { isOffline } = useContext(NetworkStatusValuesContext);
  const userContent = useContext(UserContentValuesContext);
  const { user } = useContext(UserSessionValuesContext);
  const notesAndHighlights = createNotesAndHighlightsList(
    isOffline,
    user,
    userContent,
  );

  return (
    <div className={classes.root} {...props}>
      {isOffline && <Message {...messageProps} />}
      {notesAndHighlights ? (
        <ChapterList>
          {notesAndHighlights.map(
            ([key, value]) =>
              value.length > 0 && (
                <NotesHighlightsListItem
                  defaultExpanded={defaultExpanded}
                  isOffline={isOffline}
                  key={key ? key.index : 'no-chapter'}
                  topLevelMarker={key}
                >
                  {value}
                </NotesHighlightsListItem>
              ),
          )}
        </ChapterList>
      ) : null}
    </div>
  );
};

export default memo(NotesHighlightsMenu);
